import { fabric } from 'fabric';
import { Canvas } from 'fabric/fabric-impl';
import Vec2 from 'vec2';

import {
  CANVAS_OBJECT_TYPE_PREFIX,
  IMAGE_CUSTOMER_COOKTOP,
  IMAGE_CUSTOMER_DISHWASHER,
  IMAGE_CUSTOMER_ELECTRICAL_CUTOUT,
  IMAGE_CUSTOMER_FAUCET_CUTOUT,
  IMAGE_CUSTOMER_RANGE,
  IMAGE_CUSTOMER_REFRIGERATOR,
  IMAGE_CUSTOMER_SINK_APRON,
  IMAGE_CUSTOMER_SINK_DOUBLE_BOWL,
  SELECTION_CIRCLE_OPACITY,
  SWATCH_SCALE,
} from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { SideItemOption } from '~/core/enums';
import {
  ApplianceType,
  ArrowInfo,
  ColorModel,
  DrawingAppliance,
  DrawingShape,
} from '~/core/models';
import {
  getBase64FromURL,
  imgUrl,
  isAppliance,
  isPolygonBacksplash,
  makeApplianceName,
} from '~/core/utils';

export const NO_LABEL = '';

/**
 * Calculates the slab pattern based on the slab color
 * @param color the slab swatch color
 * @param drawingShapes the list of shapes
 * @param defaultScale the `SWATCH_SCALE`
 * @returns the color pattern
 */
export const calculatePatternTransformation = (
  color: ColorModel,
  drawingShapes: DrawingShape[],
  defaultScale: number = SWATCH_SCALE
): number[] => {
  const scale = color.slabImageUrl
    ? drawingTool.calcSlabScale(drawingShapes)
    : defaultScale;

  return [1 / scale, 0, 0, 1 / scale, 0, 0];
};

/**
 * Applies the fill color to the backsplash
 * @param backsplashGroup the group of backsplash elements
 * @param imageElement the HTML image source
 * @param patternTransformation the color pattern
 */
export const applyFillToBacksplash = (
  backsplashGroup: fabric.Group,
  imageElement: HTMLImageElement,
  patternTransformation: number[]
): void => {
  const backsplashObjects = backsplashGroup.getObjects();

  backsplashObjects.forEach((child) => {
    if (isPolygonBacksplash(child)) {
      child.set(
        'fill',
        new fabric.Pattern({
          source: imageElement,
          repeat: 'repeat',
          patternTransform: patternTransformation,
        })
      );
    }
  });
};

/**
 * Applies the fill color to the main shape
 * @param mainShape the shape to update its canvas colors
 * @param shape the shape from the list of shapes
 * @param useOverlay whether to use an overlay color
 * @param imageElement the HTML image source
 * @param patternTransformation the color pattern
 */
export const applyFillToMainShape = (
  mainShape: fabric.Object,
  shape: DrawingShape,
  useOverlay: boolean,
  imageElement: HTMLImageElement,
  patternTransformation: number[]
): void => {
  const pattern = new fabric.Pattern({
    source: imageElement,
    repeat: 'repeat',
    patternTransform: patternTransformation,
  });

  mainShape.clipPath = drawingTool.getClipGroup(shape, false);

  if (useOverlay) {
    shape.fabricGroup.addWithUpdate(drawingTool.getClipGroup(shape, true));
  }

  mainShape.set('fill', pattern);
};

/**
 * Creates a clipped polygon using the `clipBoneOutline` vector and a given color
 * @param clipBoneOutline the outline of points
 * @param useOverlay whether to use an overlay color
 * @param fillColor the fill color
 * @param selectionCircleOpacity the circle opacity, defaulted to `SELECTION_CIRCLE_OPACITY`
 * @returns the clipped polygon
 */
export const createClipPolygon = (
  clipBoneOutline: Vec2[],
  useOverlay: boolean,
  fillColor: string,
  selectionCircleOpacity = SELECTION_CIRCLE_OPACITY
): fabric.Polygon => {
  const baseConfig = {
    selectable: false,
  };

  const overlayConfig = useOverlay
    ? {
        fill: fillColor,
        opacity: selectionCircleOpacity,
      }
    : {};

  return new fabric.Polygon(clipBoneOutline, {
    ...baseConfig,
    ...overlayConfig,
  });
};

/**
 * Return an array of labels mirroring the arrowsInfo array passed to it.
 * This should really return a new arrow of arrowsInfo, but there is code
 * that expects the array to be mutated in place rather than be new.
 * Returning a simple array of the labels only allows the caller to decide
 * how to use them
 * @param arrowsInfo arrows
 * @param startIndex
 */
export const getLabelsForArrows = (
  arrowsInfo: ArrowInfo[],
  startIndex: number
): string[] => {
  const letterStartIndex = startIndex;
  const [interiorCnts, diagonalCnts] = arrowsInfo.reduce<[number, number]>(
    ([interior, diagonal], arrow) => [
      interior + (arrow.isInterior ? 1 : 0),
      diagonal + (arrow.isDiagonalCorner ? 1 : 0),
    ],
    [0, 0]
  );

  let arrowIdxCnt = 0;

  return arrowsInfo.map((arrowInfo: ArrowInfo) => {
    if (!arrowInfo.isInterior && !arrowInfo.isDiagonalCorner) {
      const letterIndex =
        arrowIdxCnt <= letterStartIndex
          ? letterStartIndex - arrowIdxCnt
          : (arrowsInfo?.length ?? 0) -
            arrowIdxCnt +
            letterStartIndex -
            (interiorCnts + diagonalCnts);

      arrowIdxCnt++;

      return indexToLetterLabel(letterIndex);
    } else {
      return NO_LABEL;
    }
  });
};

/**
 * Creates excel-like/letter based labels from a numeric index
 * i.e: A, B, C,...Z, AA, AB, AC...ZZ, AAA, AAB, AAC, etc.
 * @param baseTenIndex the original index, 0-n
 */
export const indexToLetterLabel = (baseTenIndex: number): string => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const conversions = '0123456789' + alphabet.toLowerCase();
  const convertedIndex = baseTenIndex.toString(alphabet.length).split('');

  return convertedIndex.reduce((prev, cur, index) => {
    const x = conversions.indexOf(cur);

    return (
      prev + alphabet[x - Number(index === 0 && convertedIndex.length > 1)]
    );
  }, '');
};

type ApplianceImages = {
  [ApplianceType.SINK]: (appliance: DrawingAppliance) => Promise<string>;
  [ApplianceType.SINK_LABOR]: (appliance: DrawingAppliance) => string;
  [ApplianceType.DISHWASHER]: () => string;
  [ApplianceType.REFRIGERATOR]: () => string;
  [ApplianceType.COOKTOP]: () => string;
  [ApplianceType.RANGE]: () => string;
  [ApplianceType.ELECTRICAL_CUTOUT]: () => string;
  [ApplianceType.FAUCET_CUTOUT]: () => string;
  [ApplianceType.FAUCET]: undefined;
};

export const applianceImages: ApplianceImages = {
  [ApplianceType.SINK]: async (appliance: DrawingAppliance) =>
    await getBase64FromURL(
      imgUrl(appliance.applianceModel.beautyShotImageUrl, 300)
    ),
  [ApplianceType.SINK_LABOR]: (appliance: DrawingAppliance) =>
    appliance.applianceModel.name.includes('Apron')
      ? IMAGE_CUSTOMER_SINK_APRON
      : IMAGE_CUSTOMER_SINK_DOUBLE_BOWL,
  [ApplianceType.DISHWASHER]: () => IMAGE_CUSTOMER_DISHWASHER,
  [ApplianceType.REFRIGERATOR]: () => IMAGE_CUSTOMER_REFRIGERATOR,
  [ApplianceType.COOKTOP]: () => IMAGE_CUSTOMER_COOKTOP,
  [ApplianceType.RANGE]: () => IMAGE_CUSTOMER_RANGE,
  [ApplianceType.ELECTRICAL_CUTOUT]: () => IMAGE_CUSTOMER_ELECTRICAL_CUTOUT,
  [ApplianceType.FAUCET_CUTOUT]: () => IMAGE_CUSTOMER_FAUCET_CUTOUT,
  [ApplianceType.FAUCET]: undefined,
};

/**
 * Creates a group of arrows using arrow options
 * @param arrowInfo the arrow (side) info
 * @param arrowOptions the options to construct the arrow
 * @returns a group of arrows
 */
export const createArrowsGroup = (
  arrowInfo: ArrowInfo,
  arrowOptions: fabric.IObjectOptions
): fabric.Group => {
  const arrowsGroup = drawingTool.createArrowGroup(arrowInfo, arrowOptions);

  return (
    arrowsGroup ??
    new fabric.Group([], {
      selectable: false,
    })
  );
};

/**
 * convert a label (A, B, AA, etc) to the numeric index for that label
 * invalid labels will return zero
 * @param label string
 */
export const letterLabelToIndex = (label?: string): number => {
  const charA = 'A'.charCodeAt(0);
  const cleanLabel = ((label?.toString().toUpperCase() ?? '').match(
    /[A-Z]*/
  ) ?? [''])[0];
  // `A` is technically a leading zero, so force a start
  const startVal =
    cleanLabel.length > 1 ? Math.pow(26, cleanLabel.length - 1) : 0;

  return cleanLabel
    .split('')
    .reverse()
    .reduce((idx, letter, currentIndex) => {
      const letterVal = letter.charCodeAt(0) - charA;

      return idx + letterVal * Math.pow(26, currentIndex);
    }, startVal);
};

/**
 * Work backward from a "removedEdlementsHistory" to find which arrow the "A" should be.
 * Old estimates (pre 2024-10-16) did not save establishedLetterStartIndex, but if they set a side option on any
 * edge, we can back into where "A" must be.  @see EZE-1500
 */
export const getInitialLabelIndexFromRemovedHistory = (
  dShape: DrawingShape
): number | undefined => {
  if (
    !dShape.removedElementsHistory?.edgesAndCorners.length ||
    !dShape.arrowsInfo?.length
  ) {
    return undefined;
  }
  const lowestLabelInfo = dShape.removedElementsHistory.edgesAndCorners
    .filter(
      (removedEdge) =>
        !!removedEdge.arrowInfo?.label && removedEdge.drawingEdge?.id
    )
    .map((removedEdge) => ({
      label: removedEdge.arrowInfo?.label,
      edgeId: removedEdge.drawingEdge.id,
    }))
    .toSorted((arrowInfoA, arrowInfoB) => {
      const label1 = arrowInfoA.label ?? '';
      const label2 = arrowInfoB.label ?? '';
      // Sort by length and then alphabetically

      return (
        Math.sign(label1.length - label2.length) || label1.localeCompare(label2)
      );
    })[0];

  if (!lowestLabelInfo) {
    // This could happen if the arrows don't have any labels yet - probably broken
    return undefined;
  }
  const indexOfLowestLetter = letterLabelToIndex(lowestLabelInfo.label);
  const startIndex = dShape.arrowsInfo.findIndex(
    (arrowInfo) => arrowInfo.drawingEdgeId === lowestLabelInfo.edgeId
  );

  // iterate through all the arrows and skip diagonals and interiors to find the index of "A"
  // SEE: Drawingtools!!!
  let countDown = indexOfLowestLetter;
  let indexOfA = startIndex;

  while (countDown) {
    indexOfA = indexOfA + 1 >= dShape.arrowsInfo.length ? 0 : indexOfA + 1;
    const isLetterSide = !(
      dShape.arrowsInfo[indexOfA].isInterior ||
      dShape.arrowsInfo[indexOfA].isDiagonalCorner
    );

    // only continue if it's a lettered side
    countDown = countDown - Number(isLetterSide);
  }

  return indexOfA;
};

/**
 * Finds an image object by name in a collection of fabric objects.
 * @param objects - Array of fabric objects to search through
 * @param name - The name to search for
 * @returns The found fabric.Image object, or undefined if not found
 */
export const findImageByName = (
  objects: fabric.Object[],
  name: string
): fabric.Image | undefined =>
  objects.find(
    (obj): obj is fabric.Image => obj.name === name && obj.type === 'image'
  );

/**
 * Finds ALL image objects with a given name in a collection of fabric objects.
 * Unlike findImageByName which returns only the first match, this returns all matches.
 * Useful for detecting duplicates.
 * @param canvas - The fabric canvas to search
 * @param name - The name to search for
 * @returns Array of found fabric.Image objects
 */
export const findAllImagesByName = (
  canvas: fabric.Canvas,
  name: string
): fabric.Image[] =>
  canvas
    .getObjects()
    .filter(
      (obj): obj is fabric.Image => obj.name === name && obj.type === 'image'
    );

/**
 * Removes duplicate appliance objects from the canvas.
 * Keeps the first occurrence of each appliance and removes subsequent duplicates.
 * This is a safety net to clean up any duplicates that may have been created
 * by race conditions or other issues.
 * @param canvas - The fabric canvas to clean up
 * @returns The number of duplicates removed
 */
export const removeDuplicateAppliances = (canvas: fabric.Canvas): number => {
  const seenNames = new Set<string>();
  const duplicates: fabric.Object[] = [];

  canvas.getObjects().forEach((obj) => {
    if (obj.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE)) {
      if (seenNames.has(obj.name)) {
        duplicates.push(obj);
      } else {
        seenNames.add(obj.name);
      }
    }
  });

  if (duplicates.length > 0) {
    canvas.remove(...duplicates);
    console.warn(
      `Removed ${duplicates.length} duplicate appliances from canvas`
    );
  }

  return duplicates.length;
};

/**
 * Validates that canvas appliances match the data model.
 * Removes any canvas appliances that don't exist in the data model (orphaned appliances).
 * This is CRITICAL to call after operations that clone shapes, such as:
 * - Undo/Redo operations
 * - Copy/Paste operations
 * - Shape duplication
 * @param shapes - The current array of drawing shapes (data model)
 * @param canvas - The fabric canvas to validate
 * @returns The number of orphaned appliances removed
 */
export const validateApplianceConsistency = (
  shapes: DrawingShape[],
  canvas: fabric.Canvas
): void => {
  const validApplianceNames = new Set(
    shapes.flatMap((shape) => shape.appliances.map(makeApplianceName))
  );
  const orphanedAppliances = canvas
    .getObjects()
    .filter(
      (obj) => isAppliance(obj) && !validApplianceNames.has(obj.name ?? '')
    );

  if (orphanedAppliances.length > 0) {
    canvas.remove(...orphanedAppliances);
  }
};

/**
 * Draws any applicable side items
 * (e.g. Backsplash, Waterfall edges) on the canvas.
 *
 * @param {Object} params - The parameters for the function.
 * @param {DrawingShape[]} params.drawingShapes - An array of shapes to calculate area for and render side items.
 * @param {ColorModel | undefined} params.selectedColor - The currently selected color used for rendering side items.
 * @param {Canvas} params.canvas - The canvas on which to draw side items.
 * @param {DrawingShape | undefined} params.activeDrawingShape - The shape currently being edited (if any).
 * @param {(arrow: ArrowInfo, shape: DrawingShape, enteredMeasurementValue?: number) => void} params.onDimensionEdit
 *        - Callback function for when a side item dimension is edited.
 *
 * @returns {number} The total calculated area of all drawing shapes, taking the material into account.
 */
export const drawSideItems = ({
  drawingShapes,
  selectedColor,
  canvas,
  activeDrawingShape,
  onDimensionEdit,
}: {
  drawingShapes: DrawingShape[];
  selectedColor: ColorModel | undefined;
  canvas: Canvas;
  activeDrawingShape: DrawingShape | undefined;
  onDimensionEdit: (
    arrow: ArrowInfo,
    shape: DrawingShape,
    enteredMeasurementValue?: number
  ) => void;
}): void => {
  const getSideItemLabel = (type: SideItemOption) =>
    type === SideItemOption.BACKSPLASH ? 'Backsplash' : 'Waterfall\nEdge';

  selectedColor &&
    drawingShapes.forEach((shape) => {
      shape?.removedElementsHistory?.edgesAndCorners
        .filter(({ drawingEdge }) => drawingEdge?.sideItem?.depth > 0)
        .forEach(({ appliedCorners, drawingEdge }) => {
          drawingTool.drawRectangleSideItem(
            canvas,
            shape,
            appliedCorners,
            drawingEdge,
            drawingEdge.sideItem.depth,
            selectedColor,
            onDimensionEdit,
            getSideItemLabel(
              drawingEdge.sideItem.type ?? SideItemOption.BACKSPLASH
            ),
            activeDrawingShape
          );
        });
    });
};
